/* PEAR console — per-school Claude / Zana spend cap.
 *
 * Writes ONLY through /api/console/orgs/:code/zana-budget (platform admin +
 * Admin SDK). The engine re-reads organizations/{code}.zanaCapUsd on every
 * model call and clamps 0–100 again, so this slider and the live org cannot
 * drift, and a $1000 value cannot land as spend. */
import React, { useEffect, useState } from 'react';
import { fetchZanaBudget, saveZanaBudget, ZanaBudget } from '../services/consoleApi';

/* `overCap` is true for five different reasons and the panel used to print one
 * sentence for all of them: "Cap reached — engine is answering until the month
 * resets." Two of those reasons are FAULTS, not budgets, so PEAR saw "Cap
 * reached" beside "$0 spent", dragged the slider to $100, saved, and nothing
 * changed — because the real cause was a Firestore read failing. `overReason`
 * has always been on the wire; nothing rendered it. */
const OVER_COPY: Record<string, string> = {
  month_spent: 'Monthly cap reached — engine answers until the 1st. Raise the cap to restore full depth.',
  day_burst: "Today's burst limit reached — full depth returns tomorrow, or raise the monthly cap.",
  cap_zero: 'Cap is $0 — Zana is engine-only for this school by choice.',
  spend_unreadable: 'Not a budget: spend records cannot be read right now, so Zana is failing closed. Raising the cap will not help.',
  spend_unrecorded: 'Not a budget: spend writes are failing for this school, so Zana is failing closed. Raising the cap will not help.',
  budget_unreadable: 'Not a budget: the budget itself could not be read. Raising the cap will not help.',
};

const MODE_COPY: Record<ZanaBudget['mode'], string> = {
  off: 'Engine only — Claude is not called.',
  tight: 'Engine leads. Claude is saved for decisions and gaps.',
  balanced: 'Shared. Facts stay free; judgement still reaches Claude.',
  generous: 'Claude leads on advice. Engine still routes, hedges, and blocks writes.',
};

function money(n: number): string {
  const x = Number(n);
  if (!Number.isFinite(x)) return '$0';
  return `$${x.toFixed(x >= 10 || x === 0 ? 0 : 2)}`;
}

export default function ZanaCapPanel({ orgCode, card, mono }: { orgCode: string; card?: React.CSSProperties; mono?: string }) {
  const [b, setB] = useState<ZanaBudget | null>(null);
  const [draft, setDraft] = useState(15);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    setErr(null);
    fetchZanaBudget(orgCode).then((row) => {
      if (!alive) return;
      setB(row);
      if (row.ok) setDraft(row.capUsd);
      if (!row.ok) setErr(row.error || 'Could not load Zana budget.');
    });
    return () => { alive = false; };
  }, [orgCode]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    setSaved(false);
    const row = await saveZanaBudget(orgCode, draft);
    setBusy(false);
    setB(row);
    if (!row.ok) {
      setErr(row.error || 'Could not save.');
      return;
    }
    setDraft(row.capUsd);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };

  const cap = b && b.ok ? b.capUsd : draft;
  const spent = b && b.ok ? b.spentMonthUsd : 0;
  const pct = cap > 0 ? Math.min(100, Math.round((spent / cap) * 100)) : 0;
  const dirty = b && b.ok ? draft !== b.capUsd : false;

  return (
    <div style={{ ...(card || { background: 'var(--surface, #fff)', border: '1px solid var(--border, #E9E5F3)', borderRadius: 16 }), padding: '20px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Zana · Claude budget</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--t3, #5A5470)', lineHeight: 1.45, maxWidth: 520 }}>
            {`Monthly ceiling for this school, $0–${money(b && b.ok && b.hardMaxUsd ? b.hardMaxUsd : 100)}. `}
            {`The engine reads this on every ask. $0 is engine-only. A missing cap falls back to `}
            {`${money(b && b.ok && b.defaultUsd ? b.defaultUsd : 15)} — never unlimited, and a higher number `}
            {`in the database is clamped to the maximum before anything can spend it.`}
          </p>
        </div>
        {b && b.ok && b.usingDefault && (
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--amber, #8A5F14)', background: 'color-mix(in oklab, var(--amber, #C9A227) 16%, transparent)', padding: '3px 8px', borderRadius: 999 }}>
            Using $15 default
          </span>
        )}
      </div>

      {err && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--red, #B3261E)' }}>{err}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginTop: 16 }}>
        {[
          { k: 'This month', v: `${money(spent)} / ${money(cap)}` },
          { k: 'Today', v: money(b && b.ok ? b.spentTodayUsd : 0) },
          { k: 'Left', v: money(b && b.ok ? b.remainingUsd : 0) },
          { k: 'Calls', v: String((b && b.ok && b.calls) || 0) },
        ].map((s) => (
          <div key={s.k} style={{ background: 'var(--soft, #F7F5FB)', border: '1px solid var(--hairline, #EDEAF4)', borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--t3, #8E86A8)' }}>{s.k}</div>
            <div style={{ fontSize: 16, fontWeight: 800, fontFamily: mono || 'ui-monospace, monospace', marginTop: 4 }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, height: 8, borderRadius: 999, background: 'var(--soft, #EEEAF6)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 92 ? 'var(--red, #B3261E)' : 'var(--accent, #6F4FF0)' }} />
      </div>
      <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--t3, #5A5470)' }}>
        {b && b.ok ? MODE_COPY[b.mode] : 'Loading…'}
      </div>
      {b && b.ok && b.overCap && (
        <div style={{
          marginTop: 8,
          fontSize: 12.5,
          fontWeight: 600,
          color: b.overReason === 'spend_unreadable' || b.overReason === 'spend_unrecorded' || b.overReason === 'budget_unreadable'
            ? 'var(--red, #B3261E)'
            : 'var(--amber, #8A5F14)',
        }}>
          {OVER_COPY[String(b.overReason || '')] || 'Zana is engine-only for this school right now.'}
        </div>
      )}

      <label style={{ display: 'block', marginTop: 18, fontSize: 12, fontWeight: 700, color: 'var(--t2, #5A5470)' }}>
        Monthly cap · {money(draft)}
        <span style={{ fontWeight: 500, color: 'var(--t4, #8E86A8)' }}>
          {`  · about ${money(draft / 30)}/day, burst up to ${money(Math.min(draft, (draft / 30) * 2))}`}
        </span>
      </label>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={draft}
        onChange={(e) => setDraft(Number(e.target.value))}
        style={{ width: '100%', marginTop: 8, accentColor: 'var(--accent, #6F4FF0)' }}
        aria-label="Monthly Claude cap in US dollars"
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          value={draft}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (!Number.isFinite(n)) return;
            setDraft(Math.max(0, Math.min(100, n)));
          }}
          style={{ width: 88, font: 'inherit', fontFamily: mono || 'ui-monospace, monospace', fontWeight: 700, padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border, #E9E5F3)', background: 'var(--surface, #fff)' }}
        />
        <span style={{ fontSize: 12.5, color: 'var(--t3, #5A5470)' }}>USD / month</span>
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={() => void save()}
          style={{
            marginLeft: 'auto',
            font: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            padding: '9px 16px',
            borderRadius: 10,
            border: '1px solid transparent',
            background: dirty ? 'var(--accent, #6F4FF0)' : 'var(--soft, #EEEAF6)',
            color: dirty ? '#fff' : 'var(--t3, #8E86A8)',
            cursor: busy || !dirty ? 'not-allowed' : 'pointer',
          }}
        >
          {busy ? 'Saving…' : saved ? 'Saved' : 'Save cap'}
        </button>
      </div>
    </div>
  );
}
